import { toast } from './toast';
import { holeToken } from './sitzung';

export const API_BASE = process.env.REACT_APP_API_URL ?? '';

export function apiHeaders(): Record<string, string> {
  const token = holeToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// ── Welle S4: zentraler Fetch-Wrapper gegen die Silent-Failure-Klasse ───────
// Wirft bei !ok einen ApiError mit Status + Server-Meldung. Nicht geeignet für
// FormData-Uploads (Content-Type!) und Blob-Downloads — dort weiter fetch pur.

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export async function apiFetch<T = unknown>(path: string, opts: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, { headers: apiHeaders(), ...opts });
  } catch {
    throw new ApiError(0, 'Keine Verbindung zum Server. Bitte Internetverbindung prüfen.');
  }
  let data: any = null;
  try { data = await res.json(); } catch { /* leerer/kein JSON-Body */ }
  if (!res.ok) {
    const msg = data?.error === 'kontingent'
      ? data.message // S3-Kontingent: deutsche Meldung durchreichen
      : (data?.fehler ?? data?.message
        ?? (res.status === 413 ? 'Die Datei ist zu groß. Bitte eine kleinere Datei verwenden.' : `Anfrage fehlgeschlagen (${res.status}).`));
    throw new ApiError(res.status, msg, data);
  }
  pruefeKontingentWarnung(data);
  return data as T;
}

// ── Welle D2 (Fix 4): 80%-Kontingent-Warnung ────────────────────────────────
// Erfolgreiche KI-Antworten tragen ein kontingent-Feld (S3 res.json-Wrapper).
// Ab 80% Verbrauch: einmaliger Hinweis-Toast, max. 1x pro Typ pro Tag.
// Bei 100% greift ohnehin der 429-Pfad aus S3 — hier bewusst nichts gedoppelt.
const KONTINGENT_TYP_LABELS: Record<string, string> = {
  mail: 'Mail-KI', diktat: 'Diktat', schulrecht: 'Schulrecht', werkzeuge: 'Werkzeuge',
};

export function pruefeKontingentWarnung(data: unknown) {
  const k = (data as any)?.kontingent;
  if (!k || typeof k.verbraucht !== 'number' || typeof k.limit !== 'number' || k.limit <= 0) return;
  if (k.verbraucht / k.limit < 0.8 || k.verbraucht >= k.limit) return;
  const heute = new Date().toISOString().slice(0, 10);
  const key = `ld-kontingent-warnung-${k.typ}`;
  try {
    if (localStorage.getItem(key) === heute) return;
    localStorage.setItem(key, heute);
  } catch { /* localStorage nicht verfügbar → lieber warnen als schweigen */ }
  toast(
    `Dein ${KONTINGENT_TYP_LABELS[k.typ] ?? k.typ}-Kontingent ist zu 80% verbraucht (${k.verbraucht}/${k.limit}). Reset am 1. des Monats.`,
    'info',
  );
}

export const apiGet    = <T = unknown>(path: string) => apiFetch<T>(path);
export const apiPost   = <T = unknown>(path: string, body?: unknown) =>
  apiFetch<T>(path, { method: 'POST', ...(body !== undefined ? { body: JSON.stringify(body) } : {}) });
export const apiPut    = <T = unknown>(path: string, body?: unknown) =>
  apiFetch<T>(path, { method: 'PUT', ...(body !== undefined ? { body: JSON.stringify(body) } : {}) });
export const apiPatch  = <T = unknown>(path: string, body?: unknown) =>
  apiFetch<T>(path, { method: 'PATCH', ...(body !== undefined ? { body: JSON.stringify(body) } : {}) });
export const apiDelete = <T = unknown>(path: string) => apiFetch<T>(path, { method: 'DELETE' });

/**
 * Für Reads mit stillem Fallback: liefert bei !ok den Fallback, hinterlässt
 * aber eine console.warn-Debugspur (kein Toast-Spam bei Ladefehlern).
 * Verwendung: fetch(...).then(leseFallback([], '/api/noten'))
 */
export function leseFallback<T>(fallback: T, kontext: string) {
  return (r: Response): Promise<T> => {
    if (!r.ok) {
      console.warn(`[api] Laden fehlgeschlagen (${r.status}):`, kontext);
      return Promise.resolve(fallback);
    }
    return r.json() as Promise<T>;
  };
}

/** Übersetzt einen (Api-)Fehler in einen Fehler-Toast. `kontext` z. B. "Speichern fehlgeschlagen". */
export function zeigeApiFehler(err: unknown, kontext?: string) {
  const msg = err instanceof Error ? err.message : 'Unbekannter Fehler.';
  toast(kontext ? `${kontext}: ${msg}` : msg, 'error');
}
